import React from "react";

const Privacy = () => {
  return (
    <main className="privacy-page">
      <div className="privacy-container">
        <h1>GenomeIDP Privacy Policy</h1>
        <p><strong>Last updated:</strong> July 15, 2026</p>

        <p>
          GenomeIDP (“we”, “us”, “our”) is an online career guidance platform for
          students and educators. This Privacy Policy explains how we collect,
          use, disclose, and protect personal information when you use GenomeIDP,
          and describes your choices and rights.
        </p>
        <p>
          By accessing or using GenomeIDP, you agree to the collection and use of
          information in accordance with this Policy.
        </p>

        <h2>1. Scope and applicable laws</h2>
        <p>
          GenomeIDP is based in Canada and primarily serves Canadian students and
          educators, but is available to users globally. For Canadian users, we
          handle personal information in accordance with Canada’s Personal
          Information Protection and Electronic Documents Act (PIPEDA) and its
          fair information principles (accountability, identifying purposes,
          consent, limiting collection, limiting use, disclosure and retention,
          accuracy, safeguards, openness, individual access, and challenging
          compliance).
        </p>
        <p>
          If you access GenomeIDP from outside Canada, we will still follow the
          principles in this Policy. Local laws in your jurisdiction may provide
          additional rights.
        </p>

        <h2>2. Information we collect</h2>
        <p>
          We collect only the personal information that we reasonably need to
          operate the GenomeIDP platform and provide our services.
        </p>

        <h3>2.1 Account information</h3>
        <p>When you create an account, we collect:</p>
        <ul>
          <li>Username</li>
          <li>Password</li>
        </ul>
        <p>
          We do not currently collect names, email addresses inside the app, or
          other contact details as part of the GenomeIDP account itself. The
          contact email below is for privacy inquiries only, not tied to your
          user profile.
        </p>

        <h3>2.2 Assessment and learning data</h3>
        <p>When you complete GenomeIDP surveys or use the platform, we collect:</p>
        <ul>
          <li>
            <strong>Assessment responses:</strong> answers to survey questions and
            other input you provide.
          </li>
          <li>
            <strong>Scores and profiles:</strong> computed scores, profiles, and
            summaries generated by the assessment engine.
          </li>
          <li>
            <strong>Career match recommendations:</strong> recommended careers,
            fields, or pathways based on your responses.
          </li>
          <li>
            <strong>Notes and saved items:</strong> free‑text notes you write,
            saved resources, and similar metadata associated with your learning
            and career exploration.
          </li>
        </ul>
        <p>
          We do not intend to collect or store sensitive personal information such
          as health status, genetic test results, racial or ethnic origin,
          religious beliefs, sexual orientation, or government identification
          numbers.
        </p>

        <h3>2.3 Technical and usage information</h3>
        <p>
          We use plain Express and MongoDB and do not use third‑party analytics
          tools such as Google Analytics or advertising trackers. However, some
          limited technical information is automatically collected when you use
          the platform, including:
        </p>
        <ul>
          <li>IP address and basic network information</li>
          <li>Browser type and operating system (via the user‑agent string)</li>
          <li>Pages or routes visited and HTTP method (e.g., GET, POST)</li>
          <li>Timestamps and basic error information</li>
        </ul>
        <p>
          This information is typically present in standard server and hosting
          logs and is used for security, reliability, and debugging. We do not
          use this technical data to build individual marketing profiles or track
          you across other websites.
        </p>

        <h2>3. How we collect information</h2>
        <p>We collect information in three main ways:</p>
        <ol>
          <li>
            <strong>Information you provide directly:</strong> when you create an
            account or complete surveys, write notes, or save resources.
          </li>
          <li>
            <strong>Automatic collection through system logs:</strong> when your
            browser or device communicates with our servers, technical details are
            automatically included in the request and may be logged by our hosting
            environment.
          </li>
          <li>
            <strong>Derived information:</strong> when we process your assessment
            responses to produce scores, profiles, and career match
            recommendations.
          </li>
        </ol>
        <p>
          We do not currently use cookies or tracking technologies beyond what is
          required for basic session and request handling in your browser.
        </p>

        <h2>4. How we use your information</h2>
        <p>
          We use personal information only for the purposes we identify and only
          as far as necessary to fulfill those purposes.
        </p>

        <h3>4.1 Operating the GenomeIDP platform</h3>
        <p>We use your information to:</p>
        <ul>
          <li>Create and manage your GenomeIDP account.</li>
          <li>Deliver assessment questions and process your responses.</li>
          <li>Generate scores, profiles, and career match recommendations.</li>
          <li>
            Save your progress, notes, and resources so you can return to them
            later.
          </li>
          <li>
            Provide downloadable reports summarizing your results and notes.
          </li>
        </ul>

        <h3>4.2 Improving the platform</h3>
        <p>
          We may use anonymized or aggregated data to understand how assessments
          perform, where they can be improved, identify patterns and trends in
          student interests and career matches, and improve the design, content,
          and user experience of the platform. We do not use your identifiable
          personal information for targeted advertising or marketing.
        </p>

        <h3>4.3 Security and reliability</h3>
        <p>
          We may use technical and log data to monitor access to the platform and
          detect unusual activity, diagnose errors and maintain system
          performance, and protect against fraud, abuse, and security incidents.
        </p>

        <h2>5. Legal basis and consent</h2>
        <p>
          We rely on your consent and on our legitimate interest in operating an
          educational career guidance platform as the main legal bases for
          processing your information.
        </p>
        <p>
          By creating an account and using GenomeIDP, you consent to the
          collection and use of your assessment data for personalized career
          guidance, and to the basic technical logging needed to keep the service
          secure and reliable.
        </p>
        <p>
          You may withdraw your consent at any time by deleting your account (when
          this feature is available) or contacting us to request deletion or
          anonymization of your personal information as described in this Policy.
        </p>

        <h2>6. Cookies and tracking</h2>
        <p>
          GenomeIDP currently does not use marketing or analytics cookies, tracking
          pixels, or device fingerprinting technologies. If we introduce cookies
          or similar technologies in the future, we will limit them to what is
          necessary and proportionate and update this Policy to describe the types
          of cookies used and how you can control them.
        </p>

        <h2>7. Sharing and disclosure of information</h2>
        <p>We do not sell your personal information.</p>
        <p>
          We may share your information with the following categories of third
          parties, but only as necessary to provide the service:
        </p>
        <ul>
          <li>
            <strong>Hosting and infrastructure providers:</strong> Render
            (application hosting) and MongoDB (database hosting) process your data
            to store and serve the platform. They may maintain operational logs
            containing technical request details.
          </li>
          <li>
            <strong>Service providers we engage in the future:</strong> if we use
            email or logging providers later, they will only receive the
            information necessary to perform those services under appropriate
            safeguards.
          </li>
        </ul>
        <p>
          We may also disclose your information where required or permitted by
          law, such as in response to a valid legal process or to protect our
          rights, safety, or the rights and safety of others.
        </p>

        <h2>8. Data retention, deletion, and anonymization</h2>

        <h3>8.1 General retention</h3>
        <p>
          We retain personal information only as long as necessary to fulfill the
          purposes described in this Policy or as required by law. In practice,
          that means we keep account and assessment data while your account is
          active, may retain data for a reasonable period after your last use of
          the platform to allow you to return and access your results, and over
          time may delete or anonymize data associated with inactive accounts.
        </p>

        <h3>8.2 Anonymization for research</h3>
        <p>
          We are committed to retaining aggregate, non‑identifiable data for
          research and statistical purposes, while respecting your privacy. When
          we anonymize data, we remove or overwrite direct identifiers (such as
          username and any account‑linked contact details), break the link between
          your account and your assessment responses, notes, and saved resources,
          and retain only aggregated statistics or data that cannot reasonably be
          used to identify you.
        </p>

        <h3>8.3 Deletion/anonymization on request</h3>
        <p>
          You may request that your GenomeIDP account and associated personal
          information be deleted or anonymized by contacting us at{" "}
          <a href="mailto:erin.styles@utoronto.ca">erin.styles@utoronto.ca</a>.
          When we receive such a request, we will delete or remove identifying
          information for your account from our active systems and anonymize your
          assessment data so that it can no longer be linked to you personally,
          while allowing us to retain non‑identifiable aggregate information for
          research and statistical purposes.
        </p>
        <p>
          We may retain limited records if required by law or for legitimate
          reasons (for example, to resolve disputes, enforce our terms, or
          maintain aggregated platform statistics), but we will not keep
          information in a form that identifies you beyond what is necessary for
          those purposes.
        </p>

        <h2>9. Access and correction rights</h2>
        <p>
          You have the right to request access to the personal information we hold
          about you and to seek corrections if it is inaccurate or incomplete.
        </p>
        <p>
          You can access your assessment results directly within the app and via
          downloadable reports. You can also request more detailed access to or
          correction of your stored information by contacting us at{" "}
          <a href="mailto:erin.styles@utoronto.ca">erin.styles@utoronto.ca</a>.
          We may need to verify your identity before fulfilling access or
          correction requests to protect your privacy and security.
        </p>

        <h2>10. International data transfers</h2>
        <p>
          Although GenomeIDP is aimed primarily at Canadian users, our hosting
          providers may store or process data on servers located outside Canada.
          This means your personal information may be subject to the laws of those
          other jurisdictions. We will take reasonable steps to ensure that any
          service providers handling your data provide a level of protection
          comparable to that required under Canadian privacy law.
        </p>

        <h2>11. Children and students</h2>
        <p>
          GenomeIDP is designed for students exploring careers, often in
          educational settings. We expect students under the age of majority to
          use GenomeIDP with the involvement of an educator, institution, or
          parent/guardian where required by local law. We do not knowingly collect
          personal information from very young children without appropriate
          consent.
        </p>
        <p>
          If you believe a child has provided personal information to GenomeIDP
          without appropriate consent, please contact us, and we will review and
          take appropriate action, including deletion or anonymization where
          necessary.
        </p>

        <h2>12. Security safeguards</h2>
        <p>
          We take reasonable measures to protect personal information under our
          control against loss, theft, unauthorized access, disclosure, copying,
          use, or modification. Measures include the use of HTTPS to encrypt data
          in transit, access controls limiting who can access database records and
          system configurations, use of reputable hosting and database providers
          with their own security controls, and practices for securely disposing
          of data when it is no longer required.
        </p>
        <p>
          No system can be perfectly secure, but we strive to maintain appropriate
          technical and organizational safeguards for the size and nature of
          GenomeIDP.
        </p>

        <h2>13. Openness and changes to this Policy</h2>
        <p>
          We are committed to transparency about our privacy practices. We may
          update this Privacy Policy from time to time to reflect changes in our
          platform features or data practices, applicable laws and regulatory
          guidance, or our service providers or infrastructure.
        </p>
        <p>
          When we make material changes, we will update the “Last updated” date at
          the top of this page and, where appropriate, provide a notice within the
          app or via other channels. Your continued use of GenomeIDP after any
          changes to this Policy will constitute your acceptance of the updated
          Policy.
        </p>

        <h2>14. Contact and complaints</h2>
        <p>
          If you have questions, requests, or concerns about this Privacy Policy
          or our handling of personal information, please contact:
        </p>
        <p>
          <strong>Privacy Officer – GenomeIDP</strong>
          <br />
          Email:{" "}
          <a href="mailto:erin.styles@utoronto.ca">erin.styles@utoronto.ca</a>
        </p>
        <p>
          If you are not satisfied with our response, you may have the right to
          contact the Office of the Privacy Commissioner of Canada or your
          provincial privacy regulator to raise concerns or file a complaint.
        </p>
      </div>
    </main>
  );
};

export default Privacy;